// src/pages/VerifyOTP.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, ArrowRight } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const OTP_LENGTH = 6;

const VerifyOTP: React.FC = () => {
    const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    const phoneNumber =
        (location.state as any)?.phoneNumber || localStorage.getItem('temp_phone');
    const message = (location.state as any)?.message;

    useEffect(() => {
        if (!phoneNumber) navigate('/login');
    }, [phoneNumber, navigate]);

    useEffect(() => {
        inputsRef.current[0]?.focus();
    }, []);

    const code = digits.join('');
    const isFilled = code.length === OTP_LENGTH && digits.every((d) => d !== '');

    const setDigitAt = (idx: number, val: string) => {
        setDigits((prev) => {
            const next = [...prev];
            next[idx] = val;
            return next;
        });
    };

    const handleChange = (idx: number, val: string) => {
        const onlyDigits = val.replace(/\D/g, '');
        if (!onlyDigits) return setDigitAt(idx, '');

        if (onlyDigits.length > 1) {
            const arr = onlyDigits.slice(0, OTP_LENGTH);
            setDigits((prev) => {
                const merged = [...prev];
                let p = idx;
                for (let i = 0; i < arr.length && p < OTP_LENGTH; i++, p++) {
                    merged[p] = arr[i];
                }
                return merged;
            });
            const nextIndex = Math.min(idx + arr.length, OTP_LENGTH - 1);
            inputsRef.current[nextIndex]?.focus();
            return;
        }

        setDigitAt(idx, onlyDigits);
        if (onlyDigits && idx < OTP_LENGTH - 1) {
            inputsRef.current[idx + 1]?.focus();
        }
    };

    const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.key;
        if (key === 'Backspace') {
            if (digits[idx]) setDigitAt(idx, '');
            else if (idx > 0) {
                inputsRef.current[idx - 1]?.focus();
                setDigitAt(idx - 1, '');
            }
        } else if (key === 'ArrowLeft') {
            if (idx > 0) inputsRef.current[idx - 1]?.focus();
        } else if (key === 'ArrowRight') {
            if (idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
        }
    };

    const handlePaste = (idx: number, e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (!text) return;
        setDigits((prev) => {
            const next = [...prev];
            let p = idx;
            for (let i = 0; i < text.length && p < OTP_LENGTH; i++, p++) next[p] = text[i];
            return next;
        });
        const nextIndex = Math.min(idx + text.length, OTP_LENGTH - 1);
        inputsRef.current[nextIndex]?.focus();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!phoneNumber) return;
        setError('');
        setLoading(true);
        try {
            const response = await authAPI.verifyOTP(phoneNumber, code);
            if (response.token && response.buyer) {
                localStorage.removeItem('temp_phone');
                login(response.token, response.buyer);
                navigate('/dashboard');
            } else setError('پاسخ نامعتبر از سرور');
        } catch (err: any) {
            setError(err?.response?.data?.message || 'کد تأیید نامعتبر است. دوباره تلاش کنید.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (!phoneNumber) return;
        setError('');
        setLoading(true);
        try {
            await authAPI.requestOTP(phoneNumber);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'ارسال مجدد کد ناموفق بود.');
        } finally {
            setLoading(false);
        }
    };

    if (!phoneNumber) return null;

    return (
        <div
            className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4"
            dir="rtl"
        >
            <Card className="w-full max-w-sm sm:max-w-md border-green-200 shadow-lg">
                <CardHeader className="space-y-1">
                    <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 sm:mb-4 bg-green-100 rounded-full">
                        <Shield className="w-6 h-6 text-green-700" />
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-bold text-center text-green-900">
                        تأیید کد یک‌بارمصرف
                    </CardTitle>
                    <CardDescription className="text-center text-green-700 text-sm sm:text-base">
                        کد ارسال‌شده به <span className="font-medium">{phoneNumber}</span> را وارد کنید
                    </CardDescription>

                    {(message ?? '') && (
                        <Alert className="mt-2 border-green-200">
                            <AlertDescription className="text-sm sm:text-base">{message}</AlertDescription>
                        </Alert>
                    )}
                </CardHeader>

                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                        <div className="space-y-2">
                            <Label className="text-green-900 text-sm sm:text-base">کد تأیید</Label>

                            {/* ریسپانسیو: در موبایل چسبیده و فشرده، در مدیوم فاصله بیشتر */}
                            <div className="grid grid-cols-6 gap-1.5 sm:gap-2" dir="ltr">
                                {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                                    <Input
                                        key={i}
                                        ref={(el) => (inputsRef.current[i] = el)}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                                        enterKeyHint="done"
                                        maxLength={1}
                                        value={digits[i]}
                                        onChange={(e) => handleChange(i, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(i, e)}
                                        onPaste={(e) => handlePaste(i, e)}
                                        dir="ltr"
                                        className="h-10 sm:h-12 md:h-14 text-base sm:text-lg md:text-xl text-center font-mono tracking-widest
                               border-green-300 focus-visible:ring-green-600"
                                        placeholder="•"
                                        aria-label={`رقم ${i + 1}`}
                                    />
                                ))}
                            </div>
                        </div>

                        {error && (
                            <Alert variant="destructive" className="border-red-200">
                                <AlertDescription className="text-sm sm:text-base">{error}</AlertDescription>
                            </Alert>
                        )}

                        {/* دکمه‌ها ریسپانسیو: تمام‌عرض در موبایل */}
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                                type="submit"
                                className="w-full sm:flex-1 bg-green-600 hover:bg-green-700 text-white"
                                disabled={loading || !isFilled}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                        در حال تأیید…
                                    </>
                                ) : (
                                    'تأیید کد'
                                )}
                            </Button>

                            <Button
                                type="button"
                                variant="outline"
                                className="w-full sm:flex-1 border-green-300 text-green-700 hover:bg-green-50"
                                onClick={handleResendOTP}
                                disabled={loading}
                            >
                                ارسال مجدد کد
                            </Button>
                        </div>

                        <div className="flex items-center justify-center">
                            <Link
                                to="/login"
                                className="flex items-center justify-center text-sm sm:text-base text-green-800 hover:text-green-900"
                            >
                                <ArrowRight className="w-4 h-4 ml-1" />
                                بازگشت به ورود
                            </Link>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default VerifyOTP;
